import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { isFiveFieldCron } from '@elementary/common-ui/utils/customSqlTestConfig';
import cronParser from 'cron-parser';
import cronValidate from 'cron-validate';
import { UseFormReturn } from 'react-hook-form';
import {
  AddTestForm,
  CustomQueryDestination,
  CustomSqlCloudTestConfig,
  CustomSqlCloudTestSchedule,
  CustomSqlReferencedAsset,
  QualityDimension,
  Severity,
  SeverityFormField,
} from 'shared/types';
import {
  CUSTOM_CRON_FORMAT_ERROR,
  CUSTOM_CRON_INVALID_ERROR,
  CUSTOM_CRON_MIN_INTERVAL_MINUTES,
  CUSTOM_CRON_SCAN_RUNS,
  CUSTOM_SQL_CLOUD_ERROR,
  DEFAULT_CUSTOM_SQL_CONFIG,
  DEFAULT_CLOUD_SQL_TEST_SCHEDULE,
  MAX_QUERY_TIMEOUT_SECONDS,
  MAX_SAMPLE_ROW_LIMIT,
  MS_IN_MINUTE,
  QUALITY_DIMENSIONS,
} from './constants';

export const isCustomQueryDestination = (
  value: string,
): value is CustomQueryDestination => value === 'cloud' || value === 'pr';

export const isCloudSqlTestScheduleType = (
  value: string,
): value is CustomSqlCloudTestSchedule['type'] =>
  value === 'every_x_hours' ||
  value === 'daily' ||
  value === 'weekly' ||
  value === 'custom';

export const getCustomCronError = (value?: string | null): string | null => {
  if (!isFiveFieldCron(value)) return CUSTOM_CRON_FORMAT_ERROR;
  const cron = (value ?? '').trim().split(/\s+/).join(' ');
  if (!cronValidate(cron).isValid()) return CUSTOM_CRON_INVALID_ERROR;
  try {
    const iterator = cronParser.parseExpression(cron, {
      currentDate: new Date(Date.UTC(2024, 0, 1)),
      tz: 'UTC',
    });
    let previous = iterator.next().toDate().getTime();
    for (let i = 0; i < CUSTOM_CRON_SCAN_RUNS; i += 1) {
      const current = iterator.next().toDate().getTime();
      if (
        current - previous <
        CUSTOM_CRON_MIN_INTERVAL_MINUTES * MS_IN_MINUTE
      ) {
        return `Schedule interval must be at least ${CUSTOM_CRON_MIN_INTERVAL_MINUTES} minutes`;
      }
      previous = current;
    }
  } catch {
    return CUSTOM_CRON_INVALID_ERROR;
  }
  return null;
};

export const getCustomCronNextRun = (
  value?: string | null,
  timezone?: string,
): Date | null => {
  if (getCustomCronError(value) !== null) return null;
  try {
    return cronParser
      .parseExpression((value ?? '').trim().split(/\s+/).join(' '), {
        tz: timezone,
      })
      .next()
      .toDate();
  } catch {
    return null;
  }
};

export type CloudSqlTestScheduleFormValues = Pick<
  AddTestForm,
  | 'scheduleType'
  | 'intervalHours'
  | 'scheduleHour'
  | 'scheduleMinute'
  | 'scheduleDayOfWeek'
  | 'scheduleCron'
>;

export type CustomSqlConfigFormValues = Pick<
  AddTestForm,
  | 'severity'
  | 'storeRowSample'
  | 'sampleRowLimit'
  | 'queryTimeoutSeconds'
  | 'qualityDimension'
>;

export const isCustomSqlNumericConfigValid = (
  form: Pick<AddTestForm, 'sampleRowLimit' | 'queryTimeoutSeconds'>,
): boolean => {
  const sampleRowLimit =
    form.sampleRowLimit ?? DEFAULT_CUSTOM_SQL_CONFIG.sample_row_limit;
  const queryTimeoutSeconds =
    form.queryTimeoutSeconds ?? DEFAULT_CUSTOM_SQL_CONFIG.query_timeout_seconds;
  return (
    sampleRowLimit >= 1 &&
    sampleRowLimit <= MAX_SAMPLE_ROW_LIMIT &&
    queryTimeoutSeconds >= 1 &&
    queryTimeoutSeconds <= MAX_QUERY_TIMEOUT_SECONDS
  );
};

export const clampCustomSqlBoundedNumber = (
  rawValue: string,
  defaultValue: number,
  min: number,
  max: number,
): number => {
  const parsed = rawValue === '' ? Number.NaN : Number(rawValue);
  if (!Number.isFinite(parsed) || parsed < min) {
    return defaultValue;
  }
  if (parsed > max) {
    return max;
  }
  return parsed;
};

export const getCustomSqlPreviewSampleLimit = (
  sampleRowLimit: number | undefined,
): number =>
  clampCustomSqlBoundedNumber(
    sampleRowLimit == null ? '' : String(sampleRowLimit),
    DEFAULT_CUSTOM_SQL_CONFIG.sample_row_limit,
    1,
    MAX_SAMPLE_ROW_LIMIT,
  );

const CUSTOM_SQL_CLOUD_SUBMIT_FIELDS = [
  'testName',
  'customQuery',
  'customSqlPrimaryAssetId',
  'sampleRowLimit',
  'queryTimeoutSeconds',
  'scheduleCron',
] as const;

export type CustomSqlCloudSubmitField =
  typeof CUSTOM_SQL_CLOUD_SUBMIT_FIELDS[number];

export const getCustomSqlCloudSubmitErrors = (
  form: Partial<AddTestForm>,
): Partial<Record<CustomSqlCloudSubmitField, string>> => {
  const errors: Partial<Record<CustomSqlCloudSubmitField, string>> = {};

  if (!form.testName?.trim()) {
    errors.testName = 'Test name is required';
  }

  if (!form.customQuery?.trim()) {
    errors.customQuery = 'SQL query is required';
  } else if (!form.customSqlValidated) {
    errors.customQuery = 'Run query to validate';
  }

  if (form.customSqlRequiresAssetSelection && !form.customSqlPrimaryAssetId) {
    errors.customSqlPrimaryAssetId = 'Tested table is required';
  }

  const sampleRowLimit =
    form.sampleRowLimit ?? DEFAULT_CUSTOM_SQL_CONFIG.sample_row_limit;
  if (sampleRowLimit < 1 || sampleRowLimit > MAX_SAMPLE_ROW_LIMIT) {
    errors.sampleRowLimit = `Must be between 1 and ${MAX_SAMPLE_ROW_LIMIT}`;
  }

  const queryTimeoutSeconds =
    form.queryTimeoutSeconds ?? DEFAULT_CUSTOM_SQL_CONFIG.query_timeout_seconds;
  if (
    queryTimeoutSeconds < 1 ||
    queryTimeoutSeconds > MAX_QUERY_TIMEOUT_SECONDS
  ) {
    errors.queryTimeoutSeconds = `Must be between 1 and ${MAX_QUERY_TIMEOUT_SECONDS}`;
  }

  if (form.scheduleType === 'custom') {
    const cronError = getCustomCronError(form.scheduleCron);
    if (cronError) errors.scheduleCron = cronError;
  }

  return errors;
};

export const applyCustomSqlCloudSubmitErrors = (
  methods: UseFormReturn<AddTestForm>,
): boolean => {
  const errors = getCustomSqlCloudSubmitErrors(methods.getValues());
  CUSTOM_SQL_CLOUD_SUBMIT_FIELDS.forEach((field) => {
    methods.clearErrors(field);
  });
  (Object.entries(errors) as [CustomSqlCloudSubmitField, string][]).forEach(
    ([field, message]) => {
      methods.setError(field, { type: 'manual', message });
    },
  );
  return Object.keys(errors).length === 0;
};

export const clearCustomQueryValidation = (
  methods: UseFormReturn<AddTestForm>,
) => {
  methods.setValue('customQueryConverted', '');
  methods.setValue('customSqlValidated', false);
  methods.setValue('customSqlPrimaryAssetId', null);
  methods.setValue('customSqlReferencedAssets', []);
  methods.setValue('customSqlRequiresAssetSelection', false);
};

export const applyCloudValidationResult = (
  methods: UseFormReturn<AddTestForm>,
  referencedAssets: CustomSqlReferencedAsset[],
  requiresAssetSelection: boolean,
  primaryAssetId: string | null,
) => {
  methods.setValue('customSqlValidated', true);
  methods.setValue('customSqlReferencedAssets', referencedAssets);
  methods.setValue('customSqlRequiresAssetSelection', requiresAssetSelection);
  methods.setValue(
    'customSqlPrimaryAssetId',
    requiresAssetSelection ? null : primaryAssetId,
  );
};

export const seedMissingCustomSqlCloudFields = (
  methods: UseFormReturn<AddTestForm>,
  initialValues?: Partial<AddTestForm>,
) => {
  (
    [
      [
        'scheduleType',
        initialValues?.scheduleType ??
          DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleType,
      ],
      [
        'intervalHours',
        initialValues?.intervalHours ??
          DEFAULT_CLOUD_SQL_TEST_SCHEDULE.intervalHours,
      ],
      [
        'scheduleHour',
        initialValues?.scheduleHour ??
          DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleHour,
      ],
      [
        'scheduleMinute',
        initialValues?.scheduleMinute ??
          DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleMinute,
      ],
      [
        'scheduleDayOfWeek',
        initialValues?.scheduleDayOfWeek ??
          DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleDayOfWeek,
      ],
      ['scheduleCron', initialValues?.scheduleCron ?? ''],
      [
        'storeRowSample',
        initialValues?.storeRowSample ??
          DEFAULT_CUSTOM_SQL_CONFIG.store_row_sample,
      ],
      [
        'sampleRowLimit',
        initialValues?.sampleRowLimit ??
          DEFAULT_CUSTOM_SQL_CONFIG.sample_row_limit,
      ],
      [
        'queryTimeoutSeconds',
        initialValues?.queryTimeoutSeconds ??
          DEFAULT_CUSTOM_SQL_CONFIG.query_timeout_seconds,
      ],
    ] as const
  ).forEach(([field, value]) => {
    if (methods.getValues(field) == null) {
      methods.setValue(field, value);
    }
  });
};

export const buildCloudSqlTestSchedule = (
  form: CloudSqlTestScheduleFormValues,
): CustomSqlCloudTestSchedule => {
  if (form.scheduleType === 'every_x_hours') {
    return {
      type: 'every_x_hours',
      interval_hours: form.intervalHours ?? 6,
    };
  }

  if (form.scheduleType === 'weekly') {
    return {
      type: 'weekly',
      day_of_week: form.scheduleDayOfWeek ?? 1,
      hour: form.scheduleHour ?? 9,
      minute: form.scheduleMinute ?? 0,
    };
  }

  if (form.scheduleType === 'custom') {
    return {
      type: 'custom',
      cron: form.scheduleCron?.trim() ?? '',
    };
  }

  return {
    type: 'daily',
    hour: form.scheduleHour ?? 9,
    minute: form.scheduleMinute ?? 0,
  };
};

const getFailRowCountThreshold = (severity?: SeverityFormField) => {
  if (severity?.conditionalError || severity?.conditionalWarning) {
    return (
      severity.conditionalError?.value ??
      DEFAULT_CUSTOM_SQL_CONFIG.fail_row_count_threshold
    );
  }
  return DEFAULT_CUSTOM_SQL_CONFIG.fail_row_count_threshold;
};

const getWarnRowCountThreshold = (severity?: SeverityFormField) => {
  if (severity?.conditionalError || severity?.conditionalWarning) {
    return severity.conditionalWarning?.value ?? null;
  }
  return null;
};

export const toQualityDimension = (
  value?: string | null,
): QualityDimension | undefined => {
  if (!value) {
    return undefined;
  }
  const normalized = value.toLowerCase();
  return QUALITY_DIMENSIONS.find((dimension) => dimension === normalized);
};

export const cloudSqlTestScheduleToForm = (
  schedule: CustomSqlCloudTestSchedule,
): CloudSqlTestScheduleFormValues => {
  if (schedule.type === 'every_x_hours') {
    return {
      scheduleType: 'every_x_hours',
      intervalHours: schedule.interval_hours,
      scheduleHour: DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleHour,
      scheduleMinute: DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleMinute,
      scheduleDayOfWeek: DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleDayOfWeek,
      scheduleCron: '',
    };
  }

  if (schedule.type === 'weekly') {
    return {
      scheduleType: 'weekly',
      intervalHours: DEFAULT_CLOUD_SQL_TEST_SCHEDULE.intervalHours,
      scheduleHour: schedule.hour,
      scheduleMinute: schedule.minute ?? 0,
      scheduleDayOfWeek: schedule.day_of_week,
      scheduleCron: '',
    };
  }

  if (schedule.type === 'custom') {
    return {
      scheduleType: 'custom',
      intervalHours: DEFAULT_CLOUD_SQL_TEST_SCHEDULE.intervalHours,
      scheduleHour: DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleHour,
      scheduleMinute: DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleMinute,
      scheduleDayOfWeek: DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleDayOfWeek,
      scheduleCron: schedule.cron,
    };
  }

  return {
    scheduleType: 'daily',
    intervalHours: DEFAULT_CLOUD_SQL_TEST_SCHEDULE.intervalHours,
    scheduleHour: schedule.hour,
    scheduleMinute: schedule.minute ?? 0,
    scheduleDayOfWeek: DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleDayOfWeek,
    scheduleCron: '',
  };
};

export const customSqlSeverityToForm = (
  severity: string,
  failRowCountThreshold?: number | null,
  warnRowCountThreshold?: number | null,
): SeverityFormField => {
  const type: Severity =
    severity.toLowerCase() === 'warn' || severity.toLowerCase() === 'warning'
      ? 'warn'
      : 'error';
  if (
    (failRowCountThreshold == null || failRowCountThreshold === 0) &&
    warnRowCountThreshold == null
  ) {
    return { type };
  }

  return {
    type,
    ...(failRowCountThreshold != null && failRowCountThreshold !== 0
      ? { conditionalError: { operator: '>', value: failRowCountThreshold } }
      : {}),
    ...(warnRowCountThreshold != null
      ? {
          conditionalWarning: {
            operator: '>',
            value: warnRowCountThreshold,
          },
        }
      : {}),
  };
};

export type CustomSqlCloudEditSeed = {
  sql: string;
  name?: string | null;
  description?: string | null;
  schedule: CustomSqlCloudTestSchedule;
  severity: string;
  failRowCountThreshold?: number | null;
  warnRowCountThreshold?: number | null;
  storeRowSample?: boolean;
  sampleRowLimit?: number;
  queryTimeoutSeconds?: number;
  qualityDimension?: QualityDimension;
  owners?: string[];
  tags?: string[];
  primaryAsset?: { id: string; name: string } | null;
};

export const buildCustomSqlCloudEditInitialValues = (
  seed: CustomSqlCloudEditSeed,
): Partial<AddTestForm> => ({
  testType: 'customQuery',
  customQueryDestination: 'cloud',
  customQuery: seed.sql,
  testName: seed.name ?? '',
  description: seed.description ?? '',
  ...cloudSqlTestScheduleToForm(seed.schedule),
  severity: customSqlSeverityToForm(
    seed.severity,
    seed.failRowCountThreshold,
    seed.warnRowCountThreshold,
  ),
  storeRowSample:
    seed.storeRowSample ?? DEFAULT_CUSTOM_SQL_CONFIG.store_row_sample,
  sampleRowLimit:
    seed.sampleRowLimit ?? DEFAULT_CUSTOM_SQL_CONFIG.sample_row_limit,
  queryTimeoutSeconds:
    seed.queryTimeoutSeconds ?? DEFAULT_CUSTOM_SQL_CONFIG.query_timeout_seconds,
  qualityDimension: seed.qualityDimension,
  owners: seed.owners ?? [],
  tags: seed.tags ?? [],
  customSqlValidated: true,
  customSqlPrimaryAssetId: seed.primaryAsset?.id ?? null,
  customSqlReferencedAssets: seed.primaryAsset
    ? [
        {
          asset_id: seed.primaryAsset.id,
          name: seed.primaryAsset.name,
          fqn: seed.primaryAsset.name,
        },
      ]
    : [],
  customSqlRequiresAssetSelection: false,
});

export const buildCustomSqlConfig = (
  form: CustomSqlConfigFormValues,
): CustomSqlCloudTestConfig => ({
  fail_row_count_threshold: getFailRowCountThreshold(form.severity),
  warn_row_count_threshold: getWarnRowCountThreshold(form.severity),
  store_row_sample:
    form.storeRowSample ?? DEFAULT_CUSTOM_SQL_CONFIG.store_row_sample,
  sample_row_limit:
    form.sampleRowLimit ?? DEFAULT_CUSTOM_SQL_CONFIG.sample_row_limit,
  query_timeout_seconds:
    form.queryTimeoutSeconds ?? DEFAULT_CUSTOM_SQL_CONFIG.query_timeout_seconds,
  quality_dimension: form.qualityDimension,
});

export type CustomSqlPreviewStatus = 'pass' | 'fail' | 'warn';

export const previewCustomSqlTestStatus = (
  rowCount: number,
  severity?: SeverityFormField,
): CustomSqlPreviewStatus => {
  const failThreshold = getFailRowCountThreshold(severity);
  const warnThreshold = getWarnRowCountThreshold(severity);
  if (warnThreshold != null) {
    if (rowCount > failThreshold) {
      return 'fail';
    }
    if (rowCount > warnThreshold) {
      return 'warn';
    }
    return 'pass';
  }
  if (rowCount > failThreshold) {
    return severity?.type === 'warn' ? 'warn' : 'fail';
  }
  return 'pass';
};

export const getCustomSqlCloudErrorCode = (
  error: MutationError,
): number | undefined => error.response?.data?.elementary_error_code;

export const applyCustomSqlScheduleError = (
  methods: UseFormReturn<AddTestForm>,
  error: MutationError,
): boolean => {
  if (
    getCustomSqlCloudErrorCode(error) !==
      CUSTOM_SQL_CLOUD_ERROR.INVALID_SCHEDULE ||
    methods.getValues('scheduleType') !== 'custom'
  ) {
    return false;
  }
  methods.setError('scheduleCron', {
    type: 'manual',
    message: error.response?.data?.error_message || CUSTOM_CRON_INVALID_ERROR,
  });
  return true;
};

export const getCustomSqlCloudErrorCandidates = (
  error: MutationError,
): CustomSqlReferencedAsset[] => {
  const candidates = error.response?.data?.extra?.candidates;
  return Array.isArray(candidates) ? candidates : [];
};

const getRelationList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string');
};

const formatRelations = (relations: string[]): string =>
  relations.length ? relations.join(', ') : '';

const getUnresolvableRelationsMessage = (extra: {
  [key: string]: unknown;
}): string => {
  const unqualified = getRelationList(extra.unqualified_relations);
  if (unqualified.length) {
    return `Use fully qualified table names (database.schema.table): ${formatRelations(
      unqualified,
    )}`;
  }

  const unresolved = getRelationList(extra.unresolved_relations);
  if (unresolved.length) {
    return `Elementary hasn't synced these tables yet: ${formatRelations(
      unresolved,
    )}. The SQL itself is valid.`;
  }

  return "Elementary hasn't synced the referenced tables yet. The SQL itself is valid.";
};

export const getCustomSqlValidateErrorMessage = (
  error: MutationError,
): string => {
  const data = error.response?.data;
  const code = data?.elementary_error_code;

  if (code === CUSTOM_SQL_CLOUD_ERROR.STATIC_VALIDATION) {
    return data?.error_message || 'SQL failed validation';
  }

  if (code === CUSTOM_SQL_CLOUD_ERROR.UNRESOLVABLE_RELATIONS) {
    return getUnresolvableRelationsMessage(data?.extra || {});
  }

  if (code === CUSTOM_SQL_CLOUD_ERROR.NO_STATEMENT_TIMEOUT) {
    return (
      data?.error_message ||
      'Custom SQL cloud tests are not available for this warehouse because it does not support statement timeouts.'
    );
  }

  if (code === CUSTOM_SQL_CLOUD_ERROR.FEATURE_DISABLED) {
    return (
      data?.error_message || 'Data access is not enabled for this account.'
    );
  }

  return data?.error_message || 'Error processing query';
};
